/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React from "react";
import { FormattedMessage } from "react-intl";
import { dialogApi, dialogTypes } from "components/dialogs";
import { isChrome, isSafari, isEdge, isFirefox, getBrowser } from "dc-preview-utils/src/Utils/UserAgentUtils";
import { providers, discovery } from "dc-core";
import { docAPIResponseHandler } from "utils/docAPIResponseHandler";
import { getDevicePixelRatio } from "dc-preview-utils/src/Utils/DeviceUtils";
import { createURLFromBuffer } from "dc-preview-utils/src/Utils/UrlUtils";
import lzString from "lz-string";
import { getUseWindowPrint, getFallbackPrintTimeout } from "selectors/appSelectors";
import { isFilePasswordProtected, getShouldRenderAcroForm } from "selectors/documentSelectors";
import { getUseIframelessPrint, getIsFallbackPrintEnabled } from "selectors/appSelectors";
import * as stores from "stores";
import docApi from "api";
import {
  trackPrint,
  trackPDFLoadedInPrintHelperIframe,
  trackPDFNotLoadedInPrintHelperIframe,
  trackPrintORCancelDialogOpened,
  trackContentDocumentNotLoadedInIframe,
  trackPrintFailed,
  trackFallbackPrint
} from "analytics/events";
import { noop } from "utils/lodashUtils";
import { isIOS } from "dc-preview-utils/src/Utils/DeviceUtils";
import { isPersonalCommentingEnabled } from "utils/FeatureFlagUtils";
import { downloadAssetFromDocApi } from "utils/DownloadUtility";
import { triggerPreviewErrorEvent, triggerPrintActionEvent, triggerFallbackPrintEvent } from "publicApis/Events/events";
import { featureLifecycleHookController } from "publicApis/controller/featureLifecycleHookController";
import {
  featureLifecyleHooks,
  printAnalyticsEvents,
  printTriggerType,
  printFailureType,
  fallbackPrintEvent
} from "constants/enums";
import { getAssetUri } from "app/AppInitUtility";
import { executeWillPrintActions, executeDidPrintActions } from "pdfActionManager/docActionsHelper";
import { isPrintingAllowed } from "utils/DocPermissionsUtils";
import CommonDialog from "dc-preview-utils/src/components/dialog/CommonDialog";
import localizeComponent from "dc-preview-utils/src/Utils/LocalizationUtil";
import { getTotalPages } from "selectors/documentSelectors";
import loadTranslation from "utils/TranslationUtil";
import PrintHandlerCSS from "./printHandler.css";
import { getShouldRenderCommentsDropin } from "../selectors/overlayDropinSelectors";

const getFormattedMessage = (id, values) => <FormattedMessage id={id} values={values} />;

class PrintHandler {
  constructor() {
    this.reset();
    this.triggerPrintErrorEvent = this.triggerPrintErrorEvent.bind(this);
  }

  reset() {
    this.intermediateIframePromise = new Promise((resolve, reject) => {
      this._iframeLoadedCallback = iframeHandle => {
        if (iframeHandle) {
          resolve(iframeHandle);
        } else {
          reject();
        }
      };
    });
    this.pageInfoList = [];
  }

  cancelDialog() {
    dialogApi.hide();
  }

  _getDataBuffer() {
    return new Promise(resolve => {
      if (getShouldRenderAcroForm(stores) || isPersonalCommentingEnabled(stores)) {
        const decryptedBuffer = isFilePasswordProtected(stores);
        docApi
          .getContentBuffer(decryptedBuffer)
          .then(resp => {
            resolve(resp.data.buffer);
          })
          .catch(noop);
      } else {
        docApi
          .getFileBuffer()
          .then(data => {
            resolve(data);
          })
          .catch(noop);
      }
    });
  }

  async _getPublicPdfUrl() {
    const integrationProvider = await providers.integrationProvider();
    return await integrationProvider.getPublicPdfUrl();
  }

  downloadPDF() {
    return downloadAssetFromDocApi(stores, {
      triggeredFrom: "printCancelled"
    });
  }

  printHelperFrameLoaded(iframeHandle) {
    this._iframeLoadedCallback(iframeHandle);
  }

  triggerRestrictedPDFDialog() {
    dialogApi.show(localizeComponent(CommonDialog, loadTranslation), {
      title: getFormattedMessage("dialog.print.restricted.title"),
      message: getFormattedMessage("dialog.print.restricted.message"),
      variant: "error",
      mode: "alert",
      confirmLabel: getFormattedMessage("common.ok"),
      onConfirm: () => {
        dialogApi.hide();
      },
      className: PrintHandlerCSS.dialogTitle
    });
  }

  async print({ triggeredFrom }) {
    const printBrowserSupportStatus = this.shouldUseBrowserPrint();
    const isPrintAllowed = isPrintingAllowed(stores);
    const useWindowPrint = getUseWindowPrint(stores);

    trackPrint(printBrowserSupportStatus, triggeredFrom);
    if (!isPrintAllowed) {
      this.triggerRestrictedPDFDialog();
    } else if (useWindowPrint) {
      this.printWithWindow();
    } else if (printBrowserSupportStatus) {
      if (!getShouldRenderCommentsDropin(stores)) {
        this.printHelper();
      } else if (!isPersonalCommentingEnabled(stores)) {
        try {
          const hasComments = await docApi.getCommentsCount();
          const commentsProvider = await providers["comments-provider"]();
          const totalCommentsCount = await commentsProvider.getCommentCount(getAssetUri());

          if (totalCommentsCount - hasComments > 0) {
            dialogApi.show(dialogTypes.PRINT_OR_CANCEL_DIALOG, {
              printDialog: this.printHelper,
              cancelDialog: this.cancelDialog
            });
            trackPrintORCancelDialogOpened();
          } else {
            this.printHelper();
          }
        } catch (err) {
          this.printHelper();
        }
      } else {
        this.printHelper();
      }
    } else {
      dialogApi.show(dialogTypes.SAVE_OR_CANCEL_DIALOG, {
        downloadPDF: this.downloadPDF,
        cancelDialog: this.cancelDialog
      });
    }
    triggerPrintActionEvent(printBrowserSupportStatus);
  }

  shouldUseBrowserPrint() {
    if (stores && stores.appStore && getUseWindowPrint(stores)) return false;
    if (stores && stores.appStore && getUseIframelessPrint(stores)) {
      return true;
    }
    /* use browser print only on safari, chrome and edge. For chrome and edge browser or any other browser except safari on iOS,
      browser print is not supported. */
    let browserPrintEnabled = isSafari();
    if (!isIOS() && (isChrome() || isEdge() || isFirefox())) {
      browserPrintEnabled = true;
    }
    return browserPrintEnabled;
  }

  async triggerAfterPrintCallbacks(evt) {
    if (evt.origin === window.location.origin) {
      let data = evt.data;
      if (data.message === featureLifecyleHooks.PRINT_COMPLETED) {
        featureLifecycleHookController.trigger(featureLifecyleHooks.AFTER_PRINT, { success: data.success });
        window.removeEventListener("message", this.triggerAfterPrintCallbacks);
        window.removeEventListener("message", this.triggerAnaltyicsLogCallbacks);
        window.removeEventListener("message", this.triggerPrintErrorEvent);
        await executeDidPrintActions(stores);
      }
    }
  }

  handleAfterPrintCallbacks() {
    window.addEventListener("message", this.triggerAfterPrintCallbacks);
  }

  async _createDomWithImagesAndPrint(pageInfoList) {
    const documentDiv = document.createElement("div");
    documentDiv.classList.add("documentDiv");
    document.body.appendChild(documentDiv);

    const imageLoadPromises = [];

    // For each page information in the pageInfoList array, this loop prepares
    // an image element to load the page's image, creates a canvas element to
    // render the image, and manages the asynchronous loading and rendering
    // process using promises and event listeners.
    for (let i = 0; i < pageInfoList.length; i++) {
      const imageLoadPromise = new Promise(resolve => {
        const img = new Image();
        img.src = pageInfoList[i].imageURL;
        const canvasWrapper = document.createElement("div");
        canvasWrapper.className = "printedPage";
        documentDiv.append(canvasWrapper);
        const canvas = document.createElement("canvas");
        canvas.width = Math.floor(pageInfoList[i].width);
        canvas.height = Math.floor(pageInfoList[i].height);
        canvasWrapper.append(canvas);

        img.addEventListener("load", () => {
          canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve();
        });
      });

      imageLoadPromises.push(imageLoadPromise);
    }

    await Promise.all(imageLoadPromises);

    window.print();
    document.body.removeChild(documentDiv);
  }

  maximumRenderTime(pageRenderTimings) {
    if (pageRenderTimings && Object.keys(pageRenderTimings).length > 0) {
      let maxValue = 0;
      for (const key in pageRenderTimings) {
        if (pageRenderTimings.hasOwnProperty(key)) {
          maxValue = Math.max(maxValue, ...pageRenderTimings[key]);
        }
      }
      return maxValue;
    }
    return 0;
  }

  triggerFallbackPrint() {
    const timeout = getFallbackPrintTimeout(stores);
    triggerFallbackPrintEvent({
      statusInfo: fallbackPrintEvent.FALLBACK_PRINT_INITIATED,
      timeout
    });
    const pageCount = getTotalPages(stores);
    stores.documentViewStore.setFallbackPrintLoaderStatus(true);
    trackFallbackPrint(getBrowser().name);
    const timeoutPromise = new Promise(reject => {
      setTimeout(reject, timeout);
    });

    this.pageInfoList = []; // reset imagesList
    const promises = [];
    const dpi = getDevicePixelRatio();
    for (let itemIndex = 0; itemIndex < pageCount; itemIndex++) {
      const renditionPromise = docApi.getRendition(
        "page", // itemType
        itemIndex, // itemIndex
        1, // Zoom level
        0, // pagesInView.start
        pageCount - 1, // pagesInView.end
        dpi,
        5 // priority
      );

      const URLImagePromise = new Promise(res => {
        docAPIResponseHandler(
          renditionPromise,
          data => {
            const resp = data.data;
            const URLImage = createURLFromBuffer(resp.buffer, resp.mimeType);
            this.pageInfoList[resp.pageNum - 1] = {
              width: resp.width,
              height: resp.height,
              imageURL: URLImage
            };
            res();
          },
          () => {},
          () => {},
          () => {}
        );
      });
      promises.push(URLImagePromise);
    }
    Promise.race([Promise.all(promises), timeoutPromise])
      .then(([resolvedPromises]) => {
        stores.documentViewStore.setFallbackPrintLoaderStatus(false);
        triggerFallbackPrintEvent({
          statusInfo: fallbackPrintEvent.RENDITIONS_COMPLETED,
          timeout
        });
        this._createDomWithImagesAndPrint(this.pageInfoList);
      })
      .catch(error => {
        stores.documentViewStore.setFallbackPrintLoaderStatus(false);
        // Throw the event "File taking longer to print"
        triggerFallbackPrintEvent({
          statusInfo: fallbackPrintEvent.PRINT_PDF_TAKING_LONGER_TIME,
          timeout
        });
      });
  }

  triggerAnaltyicsLogCallbacks(evt) {
    if (evt.origin === window.location.origin) {
      let data = evt.data;
      switch (data.message) {
        case printAnalyticsEvents.PRINT_IFRAME_LOADED:
          trackPDFLoadedInPrintHelperIframe();
          break;
        case printAnalyticsEvents.PRINT_IFRAME_NOT_LOADED:
          trackPDFNotLoadedInPrintHelperIframe();
          break;
        case printAnalyticsEvents.PRINT_CONTENT_DOCUMENT_NOT_LOADED:
          trackContentDocumentNotLoadedInIframe();
          break;
      }
    }
  }

  handleAnalyticsLogCallbacks() {
    window.addEventListener("message", this.triggerAnaltyicsLogCallbacks);
  }

  triggerPrintErrorEvent(evt) {
    if (evt.origin === window.location.origin) {
      let data = evt.data;
      if (data.message === printTriggerType.PRINT_USING_HTML) {
        if (getIsFallbackPrintEnabled(stores)) {
          this.triggerFallbackPrint();
        }
        triggerPreviewErrorEvent({
          errorType: printFailureType.PRINT_PDF_NOT_LOADED_IN_IFRAME_ERROR
        });
      }
    }
  }

  handlePrintErrorEventCallback() {
    window.addEventListener("message", this.triggerPrintErrorEvent);
  }

  printWithoutIframe() {
    featureLifecycleHookController.trigger(featureLifecyleHooks.BEFORE_PRINT).then(async () => {
      await executeWillPrintActions(stores);

      let printWindow = null;
      this._getDataBuffer()
        .then(buffer => {
          const blob = new Blob([buffer], {
            type: "application/pdf"
          });
          // Adding window.URL as it was picking URL constructor in tests
          const pdfUrl = window.URL.createObjectURL(blob);
          printWindow = window.open(pdfUrl);
          if (isFirefox()) {
            setTimeout(() => {
              printWindow.print();
            }, 500);
          } else {
            printWindow.onload = function() {
              this.print();
            };
          }
          this.handleAfterPrintCallbacks();
        })
        .catch(() => {
          dialogApi.show(dialogTypes.SAVE_OR_CANCEL_DIALOG, {
            downloadPDF: this.downloadPDF,
            cancelDialog: this.cancelDialog,
            title: "dialog.print.iframeless.title"
          });
        });
    });
  }

  printHelper = () => {
    if (isFirefox()) {
      if (getIsFallbackPrintEnabled(stores)) {
        this.triggerFallbackPrint();
      } else {
        dialogApi.show(dialogTypes.SAVE_OR_CANCEL_DIALOG, {
          downloadPDF: this.downloadPDF,
          cancelDialog: this.cancelDialog
        });
      }
    } else if (getUseIframelessPrint(stores)) {
      this.printWithoutIframe();
    } else {
      this.printUsingIntermediateIframe();
    }
  };

  printWithWindow() {
    featureLifecycleHookController.trigger(featureLifecyleHooks.BEFORE_PRINT).then(() => {
      const previewPath = discovery.dropins.preview.public_path;
      let printHelperIframePath = `${previewPath}teamsPrintWindow.html?useWindowPrint=true`;
      try {
        this._getPublicPdfUrl().then(url => {
          const newStr = lzString.compressToEncodedURIComponent(url);
          printHelperIframePath = `${printHelperIframePath}#url=${newStr}`;
          window.open(printHelperIframePath, "_blank", "noopener,noreferrer");

          this.handleAfterPrintCallbacks();
          this.handleAnalyticsLogCallbacks();
          this.handlePrintErrorEventCallback();
        });
      } catch (err) {
        trackPrintFailed(true, true);
      }
    });
  }

  printUsingIntermediateIframe() {
    featureLifecycleHookController.trigger(featureLifecyleHooks.BEFORE_PRINT).then(() => {
      this.intermediateIframePromise.then(async iframeHandle => {
        await executeWillPrintActions(stores);

        this._getDataBuffer()
          .then(buffer => {
            this.handleAfterPrintCallbacks();
            this.handleAnalyticsLogCallbacks();
            this.handlePrintErrorEventCallback();
            iframeHandle.contentWindow.postMessage(
              {
                message: "doPrint",
                buffer
              },
              window.location.origin
            );
          })
          .catch(noop);
      });
    });
  }
}

export const printHandler = new PrintHandler();
